<script setup>
import { defineProps } from 'vue';
import { useRouter } from 'vue-router';
import PopperBadge from '@/components/PopperBadge.vue';
import { useGlobalSearch } from '@/composables/useGlobalSearch';
import CustomBadge from '@/components/CustomBadge.vue';

const router = useRouter();
const {
  getName,
  getPhysicalAddress,
  getIdentifiersHSplit,
  getHighwayStatus,
  getConnectionStatus,
} = useGlobalSearch();

const resolveUrl = (carrier) => {
  router.push({
    name: 'Carrier Brokers Show Page',
    params: { id: carrier.id },
  });
};

defineProps({
  data: {
    type: Array,
    required: true,
    default() {
      return [];
    },
  },
  loading: Boolean,
});
</script>

<template>
  <div
    v-if="!loading"
    class="m-2 flex flex-grow flex-col rounded-md border border-light-gray p-4"
    style="width: 340px"
    @click="resolveUrl(data)"
  >
    <div class="mb-3 flex items-start justify-between">
      <div class="flex flex-row text-left">
        <div class="-ml-4 -mt-4 flex items-center">
          <popper-badge
            :highway-status="getHighwayStatus(data?.broker_company_id)"
            :connection-status="getConnectionStatus(data)"
          />
        </div>
        <div>
          <h2 class="text-base text-dark-gray">
            {{ getName(data) }}
          </h2>
          <div class="flex gap-2">
            <p
              class="text-xs text-body-color"
              v-html="getIdentifiersHSplit(data.identifiers)"
            ></p>
          </div>
        </div>
      </div>
      <div class="col-span-1 px-5 text-xs text-body-color">
        <custom-badge
          v-if="!loading && data.overall_broker_assessment == 'fail'"
          bg-color-class="bg-orange-200"
          color-class="text-orange-600"
          size="md"
          class="inline-flex self-start capitalize"
          text="Fail"
        />
        <custom-badge
          v-if="!loading && data.overall_broker_assessment == 'pass'"
          bg-color-class="bg-green-200"
          color-class="text-green-600"
          size="md"
          class="inline-flex self-start capitalize"
          text="Pass"
        />
        <custom-badge
          v-if="!loading && data.overall_broker_assessment == 'incomplete'"
          bg-color-class="bg-placeholder-gray"
          color-class="text-dark-gray"
          size="md"
          class="inline-flex self-start capitalize"
          text="Incomplete"
        />
      </div>
    </div>
    <div class="grid grid-cols-2">
      <div class="text-left">
        <label class="text-sm text-dark-gray">HQ</label>
        <p class="text-xs text-body-color">
          {{ getPhysicalAddress(data.physical_address) }}
        </p>
      </div>
      <div class="text-left">
        <label class="text-sm text-dark-gray">Authority Age</label>
        <p class="text-xs text-body-color">
          {{ data.authority_age }}
        </p>
      </div>
    </div>
  </div>
  <div
    v-else
    class="m-2 flex flex-grow flex-col rounded-md border border-light-gray p-4"
    style="width: 340px"
  >
    <div class="mb-3 flex items-start justify-between">
      <div class="flex flex-row text-left">
        <div class="-ml-4 -mt-4 flex animate-pulse items-center">
          <popper-badge
            :highway-status="null"
            :connection-status="null"
          />
        </div>
        <div>
          <p class="loading-gradient mr-3 h-4 w-full rounded-md" />
          <div class="flex gap-2">
            <div class="text-xs text-body-color">
              <p class="loading-gradient mr-3 mt-1 h-2 w-16 rounded-md" />
            </div>
            <div class="text-xs text-body-color">
              <p class="loading-gradient mr-3 mt-1 h-2 w-16 rounded-md" />
            </div>
          </div>
        </div>
      </div>
      <div>
        <p class="loading-gradient -mr-2 h-5 w-10 rounded-full" />
      </div>
    </div>
    <div class="grid grid-cols-2">
      <div class="text-left">
        <label class="text-sm text-placeholder-gray">HQ</label>
        <div class="text-xs text-body-color">
          <p class="loading-gradient mr-3 mt-1 h-2 w-16 rounded-md" />
        </div>
      </div>
      <div class="text-left">
        <label class="text-sm text-placeholder-gray">Authority Age</label>
        <div class="text-xs text-body-color">
          <p class="loading-gradient mr-3 mt-1 h-2 w-16 rounded-md" />
        </div>
      </div>
    </div>
    <div class="mt-3 grid grid-cols-2">
      <div class="text-left">
        <label class="text-sm text-placeholder-gray">Billing Contact</label>
        <div class="flex gap-1 text-left">
          <svg
            class="text-placeholder-gray"
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
          >
            <path
              d="M8 4V12M6 10.1213L6.586 10.5607C7.36667 11.1467 8.63267 11.1467 9.414 10.5607C10.1953 9.97467 10.1953 9.02533 9.414 8.43933C9.024 8.146 8.512 8 8 8C7.51667 8 7.03333 7.85333 6.66467 7.56067C5.92733 6.97467 5.92733 6.02533 6.66467 5.43933C7.402 4.85333 8.598 4.85333 9.33533 5.43933L9.612 5.65933M14 8C14 8.78793 13.8448 9.56815 13.5433 10.2961C13.2417 11.0241 12.7998 11.6855 12.2426 12.2426C11.6855 12.7998 11.0241 13.2417 10.2961 13.5433C9.56815 13.8448 8.78793 14 8 14C7.21207 14 6.43185 13.8448 5.7039 13.5433C4.97595 13.2417 4.31451 12.7998 3.75736 12.2426C3.20021 11.6855 2.75825 11.0241 2.45672 10.2961C2.15519 9.56815 2 8.78793 2 8C2 6.4087 2.63214 4.88258 3.75736 3.75736C4.88258 2.63214 6.4087 2 8 2C9.5913 2 11.1174 2.63214 12.2426 3.75736C13.3679 4.88258 14 6.4087 14 8Z"
              stroke="currentColor"
              stroke-width="1.33333"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
          <span class="text-xs text-badge-link-yellow underline">
            <p class="loading-gradient mr-3 mt-1 h-2 w-24 rounded-md" />
          </span>
        </div>
      </div>
      <div class="text-left">
        <label class="text-sm text-placeholder-gray">Support Contact</label>
        <div class="text-xs">
          <div class="flex gap-1">
            <svg
              class="text-placeholder-gray"
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
            >
              <path
                d="M2.66667 12H13.3333C13.7015 12 14 11.7015 14 11.3333V4.66667C14 4.29848 13.7015 4 13.3333 4H2.66667C2.29848 4 2 4.29848 2 4.66667V11.3333C2 11.7015 2.29848 12 2.66667 12Z"
                stroke="currentColor"
                stroke-width="1.33333"
              />
              <path
                d="M2 4L8 8L14 4"
                stroke="currentColor"
                stroke-width="1.33333"
              />
            </svg>
            <span class="text-badge-link-yellow underline">
              <p class="loading-gradient mr-3 mt-1 h-2 w-24 rounded-md" />
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
